import { DateTime } from 'luxon';
import React, { ReactElement, useEffect } from 'react';
import { Button, ButtonGroup, Col, Dropdown, Row } from 'react-bootstrap';
import { BiBuildings, BiCheckCircle, BiNote, BiPlus, BiUser, BiXCircle } from 'react-icons/all';
import { useNavigate } from 'react-router';
import { LinkContainer } from 'react-router-bootstrap';
import { CellProps } from 'react-table';
import { Client } from '../../../../../common/src/entities';
import { useDeleteEntite, useEntites } from '../../../Utils/api';
import { useAlertDialog } from '../../../Utils/dialogContext';
import PopoverSimple from '../../Common/PopoverSimple';
import TooltipSimple from '../../Common/TooltipSimple';
import { CopierPressePapier } from '../Utils/CopierPressePapier';
import EnteteDePage from '../Utils/EnteteDePage';
import TableSimple from '../Utils/TableSimple';
import { Colonne } from '../Utils/TypesTableSimple';
import sw from 'stopword';

export default function ListeClients(): ReactElement {
    const bsAlert = useAlertDialog();
    const navigate = useNavigate();
    const [supprimerClientQuery] = useDeleteEntite(Client);
    const clients = useEntites(Client);

    useEffect(() => {
        document.title = 'Clients - Dashboard';
    }, []);

    const colonnes: Colonne<Client>[] = [
        {
            Header: 'Nom',
            accessor: 'name',
            Cell: function CelluleNom({ row }: CellProps<Client>) {
                const prefix = row.original.prefix;
                return prefix ? `${prefix} ${row.original.name}` : row.original.name;
            },
            sortType: (rowA, rowB) => {
                const phraseA = rowA.original.name.split(' ');
                const phraseB = rowB.original.name.split(' ');
                const nomA = sw.removeStopwords(phraseA, sw.fr);
                const nomB = sw.removeStopwords(phraseB, sw.fr);
                return nomA[0].localeCompare(nomB[0], 'fr-CA');
            },
        },
        {
            Header: 'Centre de service scolaire',
            Cell: ({ row }: CellProps<Client>) => {
                let name = row.original.centre_service_scolaire?.name;
                if (!name) return '';
                name = row.original.centre_service_scolaire?.name.replace('Centre de services scolaire', 'CSS');

                return name;
            },
        },
        {
            Header: '#Moy.',
            accessor: 'nb_avg_pedago',
        },
        {
            Header: 'Abonné',
            Cell: function CelluleAcces({ row }: CellProps<Client>) {
                const finAbonnement = row.original.fin_abonnement
                    ? DateTime.fromISO(row.original.fin_abonnement).toLocaleString(DateTime.DATE_SHORT)
                    : '';
                return DateTime.fromISO(row.original.fin_abonnement) > DateTime.local() ? (
                    <>
                        <TooltipSimple inList texte="Abonné">
                            <BiCheckCircle color="green" />
                        </TooltipSimple>
                        <span>{finAbonnement}</span>
                    </>
                ) : (
                    <>
                        <TooltipSimple inList texte="Non-abonné">
                            <BiXCircle color="red" />
                        </TooltipSimple>
                        <span>{finAbonnement}</span>
                    </>
                );
            },
            disableSortBy: false,
        },
        {
            Header: 'Contacts',
            Cell: function CelluleContacts({ row }: CellProps<Client>) {
                return row.original.contacts ? (
                    <PopoverSimple
                        inList
                        texte={
                            <>
                                <ul>
                                    {row.original.contacts.map((x) => (
                                        <li key={x.fk_id_contact}>{x.contact?.name}</li>
                                    ))}
                                </ul>
                            </>
                        }
                        titre="Contacts"
                    >
                        <BiUser />
                    </PopoverSimple>
                ) : null;
            },
        },
        {
            Header: 'Lieux',
            Cell: function CelluleLieux({ row }: CellProps<Client>) {
                return row.original.lieux ? (
                    <CopierPressePapier texte={row.original?.lieux?.map((x) => x.lieu.name).join('\n')}>
                        <PopoverSimple
                            inList
                            titre="Lieux"
                            texte={
                                <>
                                    <ul>
                                        {row.original.lieux.map((x) => (
                                            <li key={x.lieu.id}>{x.lieu.name}</li>
                                        ))}
                                    </ul>
                                </>
                            }
                        >
                            <BiBuildings />
                        </PopoverSimple>
                    </CopierPressePapier>
                ) : null;
            },
        },
        {
            Cell: function CelluleNotes({ value }: CellProps<Client>) {
                return value ? (
                    <PopoverSimple inList texte={value} titre="Notes">
                        <BiNote />
                    </PopoverSimple>
                ) : null;
            },
            Header: 'Notes',
            accessor: 'notes',
            disableSortBy: false,
        },
    ];

    const deleteClient = async (c: Client) => {
        if (
            window.confirm(
                'Etes-vous sur de vouloir supprimer ce client? Tous les clients associés à ce client perdront leurs droits.',
            )
        ) {
            try {
                await supprimerClientQuery(c.id);
            } catch (error) {
                const { response } = error;
                if (response.status === 409) {
                    bsAlert("D'autres entités dépendent de ce client, impossible de le supprimer", 'warning');
                } else {
                    bsAlert(`Erreur dans la suppression du client: ${error.message}`, 'danger');
                }
            }
        }
    };

    return (
        <>
            <Row>
                <Col>
                    <EnteteDePage texte="Clients" cacherBoutonRetour />
                </Col>
                <Col xs="auto">
                    <LinkContainer to="new">
                        <ButtonGroup>
                            <Button size="sm" variant="outline-primary" className="d-flex align-items-center">
                                <BiPlus /> Ajouter un client
                            </Button>
                        </ButtonGroup>
                    </LinkContainer>
                </Col>
            </Row>
            <TableSimple
                colonnes={colonnes}
                resultat={clients}
                lien="id"
                hover
                affichageMenu={(client) => (
                    <>
                        <Dropdown.Item onClick={() => navigate(String(client.id))}>Modifier</Dropdown.Item>
                        <Dropdown.Item onClick={() => deleteClient(client)} className="text-danger">
                            Supprimer
                        </Dropdown.Item>
                    </>
                )}
                etatInitial={{
                    sortBy: [
                        {
                            id: 'name',
                        },
                    ],
                }}
            />
        </>
    );
}
